import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fmtDateBoth } from '../utils/dates';

const API = process.env.REACT_APP_API || '/api';
const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];

export default function Transcription() {
  const { api, apiUpload, token, user } = useAuth();
  const toast = useToast();
  const [transcriptions, setTranscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [title, setTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [editingText, setEditingText] = useState(false);
  const [inputMode, setInputMode] = useState('file');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [directUrl, setDirectUrl] = useState('');
  const [directLoading, setDirectLoading] = useState(false);
  const [ytLoading, setYtLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  const [search, setSearch] = useState('');
  const [filterFolder, setFilterFolder] = useState('');
  const [filterApproved, setFilterApproved] = useState('');
  const [filterTag, setFilterTag] = useState('');

  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [pickFolder, setPickFolder] = useState('');

  const [shares, setShares] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);

  const [batchProgress, setBatchProgress] = useState(null);
  const audioRef = useRef(null);
  const activeWordRef = useRef(null);

  const isManager = ['admin', 'supervisor'].includes(user?.role);

  const buildQs = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filterFolder) params.set('folder_id', filterFolder);
    if (filterApproved) params.set('approved', filterApproved);
    if (filterTag) params.set('tag_id', filterTag);
    return params.toString() ? `?${params}` : '';
  };

  const load = (qs) => api(`/transcriptions${qs ?? buildQs()}`).then(setTranscriptions).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    load();
    api('/folders').then(setFolders).catch(() => {});
    api('/tags').then(setTags).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterFolder, filterApproved, filterTag]);

  const onSearchKey = (e) => { if (e.key === 'Enter') load(); };

  useEffect(() => {
    const hasActive = transcriptions.some(t => t.status === 'processing' || t.status === 'queued' || (t.chunks_total > 0 && t.chunks_done < t.chunks_total));
    if (!hasActive) return;
    const timer = setTimeout(() => load(), 5000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [transcriptions]);

  useEffect(() => {
    if (!selected || (selected.status !== 'processing' && selected.status !== 'queued')) return;
    const timer = setTimeout(() => selectTr({ id: selected.id }), 5000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [selected]);

  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = playbackRate; }, [playbackRate, selected]);

  const words = useMemo(() => {
    if (!selected?.words_json) return null;
    try { return JSON.parse(selected.words_json); } catch { return null; }
  }, [selected]);

  const chapters = useMemo(() => {
    if (!selected?.chapters_json) return [];
    try { const c = JSON.parse(selected.chapters_json); return Array.isArray(c) ? c : []; } catch { return []; }
  }, [selected]);

  const keywords = useMemo(() => {
    if (!selected?.keywords_json) return [];
    try { const k = JSON.parse(selected.keywords_json); return Array.isArray(k) ? k : []; } catch { return []; }
  }, [selected]);

  const alignedTokens = useMemo(() => {
    const text = selected?.formatted_text || selected?.raw_text || '';
    if (!text) return [];
    const parts = text.split(/(\s+)/);
    const wordCount = parts.filter(p => p.trim()).length;
    const raw = words || [];
    let wordPos = 0;
    return parts.map((part) => {
      if (!part.trim()) return { text: part, isWhitespace: true };
      const rawIdx = raw.length ? Math.min(raw.length - 1, Math.floor((wordPos / Math.max(1, wordCount)) * raw.length)) : -1;
      const rw = rawIdx >= 0 ? raw[rawIdx] : null;
      wordPos++;
      return { text: part, isWhitespace: false, start: rw?.start ?? null, end: rw?.end ?? null };
    });
  }, [selected, words]);

  const isVideo = useMemo(() => /\.(mp4|webm|mov|m4v|avi|mkv)(\?|$)/i.test(selected?.audio_url || ''), [selected]);

  useEffect(() => {
    if (activeWordRef.current) activeWordRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentTime]);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append('audio', file);
    fd.append('title', file.name.replace(/\.[^/.]+$/, ''));
    if (pickFolder) fd.append('folder_id', pickFolder);
    const result = await apiUpload('/transcriptions', fd);
    await api(`/transcriptions/${result.id}/transcribe`, { method: 'POST' });
    return result;
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';
    try {
      setProcessing(true);
      if (files.length === 1) {
        const file = files[0];
        const fd = new FormData();
        fd.append('audio', file);
        fd.append('title', title || file.name);
        if (pickFolder) fd.append('folder_id', pickFolder);
        const result = await apiUpload('/transcriptions', fd);
        await api(`/transcriptions/${result.id}/transcribe`, { method: 'POST' });
        setTitle('');
      } else {
        setBatchProgress({ done: 0, total: files.length });
        for (let i = 0; i < files.length; i++) {
          try { await uploadOne(files[i]); } catch (err) { toast.error(`فشل ملف "${files[i].name}": ${err.message}`); }
          setBatchProgress({ done: i + 1, total: files.length });
        }
        toast.success(`اكتمل رفع ${files.length} ملف`);
        setBatchProgress(null);
      }
      load();
    } catch (err) { toast.error(err.message); }
    finally { setProcessing(false); }
  };

  const handleYoutube = async () => {
    if (!youtubeUrl.trim()) return;
    setYtLoading(true);
    try {
      const body = { youtube_url: youtubeUrl, title: title || youtubeUrl };
      if (pickFolder) body.folder_id = pickFolder;
      const res = await fetch(`${API}/transcriptions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = `خطأ ${res.status}`;
        try { const e = await res.json(); msg = e.error || msg; } catch {}
        throw new Error(msg);
      }
      const result = await res.json();
      setYoutubeUrl(''); setTitle(''); load();
      setProcessing(true);
      await api(`/transcriptions/${result.id}/transcribe`, { method: 'POST' });
      load();
    } catch (err) { toast.error(err.message); }
    finally { setYtLoading(false); setProcessing(false); }
  };

  const handleDirectUrl = async () => {
    if (!directUrl.trim()) return;
    setDirectLoading(true);
    try {
      const body = { direct_url: directUrl.trim(), title: title || directUrl };
      if (pickFolder) body.folder_id = pickFolder;
      const res = await fetch(`${API}/transcriptions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = `خطأ ${res.status}`;
        try { const e = await res.json(); msg = e.error || msg; } catch {}
        throw new Error(msg);
      }
      const result = await res.json();
      setDirectUrl(''); setTitle(''); load();
      setProcessing(true);
      await api(`/transcriptions/${result.id}/transcribe`, { method: 'POST' });
      load();
    } catch (err) { toast.error(err.message); }
    finally { setDirectLoading(false); setProcessing(false); }
  };

  const selectTr = async (tr) => {
    const full = await api(`/transcriptions/${tr.id}`);
    setSelected(full);
    setEditText(full.formatted_text || full.raw_text || '');
    setCurrentTime(0); setIsPlaying(false); setEditingText(false);
    setShowRevisions(false); setShowShareModal(false);
  };

  const seekToWord = (start) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = start;
    if (!isPlaying) { audioRef.current.play(); setIsPlaying(true); }
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const total = Math.floor(s);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const buildYoutubeChapters = () => {
    if (!chapters.length) return '';
    const sorted = [...chapters].sort((a, b) => (a.start || 0) - (b.start || 0));
    const lines = [];
    if ((sorted[0].start || 0) > 1) lines.push(`0:00 ${sorted[0].title || 'المقدمة'}`);
    for (const c of sorted) lines.push(`${formatTime(c.start || 0)} ${c.title || ''}`.trim());
    return lines.join('\n');
  };

  const copyYoutubeChapters = async () => {
    const text = buildYoutubeChapters();
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      }
      toast.success('تم نسخ الفهرس — الصقه في وصف اليوتيوب');
    } catch (e) { toast.error('تعذّر النسخ'); }
  };

  const saveText = async (note) => {
    await api(`/transcriptions/${selected.id}`, { method: 'PUT', body: JSON.stringify({ formatted_text: editText, note }) });
    toast.success('تم الحفظ');
    selectTr({ id: selected.id });
  };

  const updateMeta = async (changes) => {
    const next = { ...selected, ...changes };
    setSelected(next);
    await api(`/transcriptions/${selected.id}`, { method: 'PUT', body: JSON.stringify(changes) });
    load();
  };

  const toggleTagOnSelected = async (tagId) => {
    const current = (selected.tags || []).map(t => t.id);
    const next = current.includes(tagId) ? current.filter(x => x !== tagId) : [...current, tagId];
    await api(`/transcriptions/${selected.id}`, { method: 'PUT', body: JSON.stringify({ tag_ids: next }) });
    selectTr({ id: selected.id });
  };

  const retranscribe = async (id) => {
    setProcessing(true);
    try {
      await api(`/transcriptions/${id}/transcribe`, { method: 'POST' });
      load();
      if (selected?.id === id) selectTr({ id });
    } catch (err) { toast.error(err.message); }
    setProcessing(false);
  };

  const refreshAi = async () => {
    setProcessing(true);
    try {
      await api(`/transcriptions/${selected.id}/refresh-ai`, { method: 'POST' });
      toast.success('تم تحديث الملخّص والفهرس');
      selectTr({ id: selected.id });
    } catch (err) { toast.error(err.message); }
    setProcessing(false);
  };

  const approveTr = async () => {
    await api(`/transcriptions/${selected.id}/approve`, { method: 'POST' });
    toast.success('تم اعتماد التفريغ');
    selectTr({ id: selected.id }); load();
  };

  const unapproveTr = async () => {
    await api(`/transcriptions/${selected.id}/unapprove`, { method: 'POST' });
    selectTr({ id: selected.id }); load();
  };

  const deleteTr = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('هل أنت متأكد من حذف هذا التفريغ؟')) return;
    try {
      await api(`/transcriptions/${id}`, { method: 'DELETE' });
      if (selected?.id === id) setSelected(null);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const exportFile = (format) => {
    if (!selected) return;
    const url = `${API}/transcriptions/${selected.id}/export/${format}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : Promise.reject(r))
      .then(blob => {
        const a = document.createElement('a');
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        const ext = format === 'docx' ? 'doc' : (format === 'chapters' ? 'txt' : (format === 'pdf' ? 'html' : format));
        a.download = `${selected.title || 'transcript'}${format === 'chapters' ? '-chapters' : ''}.${ext}`;
        a.click();
        URL.revokeObjectURL(objUrl);
      })
      .catch(() => toast.error('فشل التصدير'));
  };

  const openShares = async () => {
    setShowShareModal(true);
    try { setShares(await api(`/transcriptions/${selected.id}/shares`)); } catch (e) { toast.error(e.message); }
  };
  const createShare = async (password, expiresIn) => {
    try {
      const body = {};
      if (password) body.password = password;
      if (expiresIn) body.expires_in = +expiresIn;
      const r = await api(`/transcriptions/${selected.id}/shares`, { method: 'POST', body: JSON.stringify(body) });
      toast.success('تم إنشاء الرابط');
      setShares(await api(`/transcriptions/${selected.id}/shares`));
      try { await navigator.clipboard.writeText(r.url); toast.success('تم نسخ الرابط للحافظة'); } catch {}
    } catch (e) { toast.error(e.message); }
  };
  const deleteShare = async (id) => {
    await api(`/shares/${id}`, { method: 'DELETE' });
    setShares(await api(`/transcriptions/${selected.id}/shares`));
  };

  const openRevisions = async () => {
    setShowRevisions(true);
    try { setRevisions(await api(`/transcriptions/${selected.id}/revisions`)); } catch (e) { toast.error(e.message); }
  };
  const restoreRevision = async (revId) => {
    if (!window.confirm('استعادة هذه المراجعة ستستبدل النصّ الحالي. متابعة؟')) return;
    await api(`/transcriptions/${selected.id}/revisions/${revId}/restore`, { method: 'POST' });
    toast.success('تم الاستعادة');
    selectTr({ id: selected.id });
    setShowRevisions(false);
  };

  const refs = selected?.references_json && Array.isArray(selected.references_json) ? selected.references_json : [];

  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>🎙️ التفريغ النصي</h2>

      <div className="transcription-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>تفريغ جديد</div>

            <div style={{ display: 'flex', background: 'var(--gray-100)', borderRadius: 8, padding: 3, marginBottom: 12, gap: 3 }}>
              {[
                { key: 'file', label: '🎙️ ملف' },
                { key: 'youtube', label: '▶️ يوتيوب' },
                { key: 'url', label: '🔗 رابط' },
              ].map(opt => (
                <button key={opt.key} onClick={() => setInputMode(opt.key)} style={{
                  flex: 1, padding: '6px 8px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  background: inputMode === opt.key ? 'var(--surface)' : 'transparent',
                  color: inputMode === opt.key ? 'var(--primary)' : 'var(--text-muted)',
                  boxShadow: inputMode === opt.key ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
                }}>{opt.label}</button>
              ))}
            </div>

            <div className="form-group">
              <input className="form-input" placeholder="عنوان (اختياري)" value={title} onChange={e => setTitle(e.target.value)} />
            </div>

            <div className="form-group">
              <select className="form-select" value={pickFolder} onChange={e => setPickFolder(e.target.value)}>
                <option value="">— بدون مجلّد —</option>
                {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>

            {inputMode === 'file' && (
              <>
                <label className="btn btn-primary" style={{ display: 'block', textAlign: 'center', cursor: processing ? 'not-allowed' : 'pointer', opacity: processing ? .7 : 1 }}>
                  {processing ? (batchProgress ? `⏳ ${batchProgress.done}/${batchProgress.total}` : '⏳ جاري التفريغ...') : '🎙️ اختر ملفاً أو أكثر'}
                  <input type="file" accept="audio/*,video/*" multiple onChange={handleUpload} style={{ display: 'none' }} disabled={processing} />
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 6 }}>يمكن اختيار عدّة ملفات معاً</div>
              </>
            )}

            {inputMode === 'youtube' && (
              <div>
                <input className="form-input" placeholder="https://youtu.be/..." value={youtubeUrl} onChange={e => setYoutubeUrl(e.target.value)} style={{ marginBottom: 8 }} disabled={ytLoading || processing} dir="ltr"/>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleYoutube} disabled={ytLoading || processing || !youtubeUrl.trim()}>
                  {ytLoading ? '⏳ جاري التحميل...' : processing ? '⏳ جاري التفريغ...' : '▶️ تفريغ من يوتيوب'}
                </button>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>⚠️ في حال فشل يوتيوب، استخدم تبويب "🔗 رابط" مع cobalt.tools</div>
              </div>
            )}

            {inputMode === 'url' && (
              <div>
                <input className="form-input" placeholder="https://...mp3" value={directUrl} onChange={e => setDirectUrl(e.target.value)} style={{ marginBottom: 8 }} disabled={directLoading || processing} dir="ltr"/>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleDirectUrl} disabled={directLoading || processing || !directUrl.trim()}>
                  {directLoading ? '⏳ جاري التحميل...' : processing ? '⏳ جاري التفريغ...' : '🔗 تحميل وتفريغ'}
                </button>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.7, padding: 8, background: 'var(--gray-50)', borderRadius: 6, border: '1px solid var(--gray-200)' }}>
                  <strong>لفيديو يوتيوب:</strong> افتح <a href="https://cobalt.tools" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>cobalt.tools</a> ← الصق الرابط ← اختر audio/mp3 ← انسخ رابط التحميل والصقه هنا.
                </div>
              </div>
            )}
          </div>

          <div className="card" style={{ marginBottom: 12, padding: 12 }}>
            <input className="form-input" placeholder="🔍 ابحث..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={onSearchKey} style={{ marginBottom: 8 }}/>
            <select className="form-select" value={filterFolder} onChange={e => setFilterFolder(e.target.value)} style={{ marginBottom: 8 }}>
              <option value="">كل المجلّدات</option>
              {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <select className="form-select" value={filterTag} onChange={e => setFilterTag(e.target.value)} style={{ marginBottom: 8 }}>
              <option value="">كل الوسوم</option>
              {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select className="form-select" value={filterApproved} onChange={e => setFilterApproved(e.target.value)}>
              <option value="">الكلّ</option>
              <option value="1">المعتمدة فقط</option>
              <option value="0">غير المعتمدة</option>
            </select>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>التفريغات ({transcriptions.length})</div>
            {loading ? <div className="loader">جاري التحميل...</div> : transcriptions.length === 0 ? (
              <div className="empty" style={{ padding: 20 }}>لا توجد نتائج</div>
            ) : (
              transcriptions.map(tr => (
                <div key={tr.id} onClick={() => selectTr(tr)}
                  style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selected?.id === tr.id ? 'var(--primary-light)' : undefined, borderRadius: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontWeight: 500, fontSize: 14, flex: 1 }}>
                      {tr.is_approved && <span title="مُراجَع ومعتمد" style={{ color: 'var(--primary)', marginInlineStart: 4 }}>✔</span>}
                      {tr.title}
                    </div>
                    {isManager && (
                      <button onClick={(e) => deleteTr(tr.id, e)} title="حذف" style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 14 }}>🗑️</button>
                    )}
                  </div>
                  {(tr.folder?.name || (tr.tags && tr.tags.length)) && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {tr.folder?.name && <span>📂 {tr.folder.name}</span>}
                      {(tr.tags || []).map(t => (
                        <span key={t.id} style={{ padding: '0 6px', borderRadius: 4, background: t.color || 'var(--gray-100)', color: 'var(--text)' }}>#{t.name}</span>
                      ))}
                    </div>
                  )}
                  {tr.chunks_total > 0 && tr.chunks_done < tr.chunks_total && (
                    <div style={{ fontSize: 11, color: 'var(--primary)', marginTop: 2 }}>🧩 {tr.chunks_done}/{tr.chunks_total} أجزاء</div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span className={`badge badge-${tr.status === 'completed' ? 'completed' : tr.status === 'processing' ? 'in_progress' : tr.status === 'failed' ? 'revision' : 'pending'}`}>
                      {tr.status === 'completed' ? (tr.is_approved ? 'مُعتمد' : 'مكتمل') : tr.status === 'processing' ? (tr.chunks_total > 0 ? `جزء ${tr.chunks_done}/${tr.chunks_total}` : 'جاري') : tr.status === 'failed' ? 'فشل' : 'جديد'}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{fmtDateBoth(tr.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!selected ? (
            <div className="card">
              <div className="empty"><div className="empty-icon">🎙️</div>اختر تفريغاً أو ارفع ملفاً صوتياً جديداً</div>
            </div>
          ) : (
            <>
              {selected.audio_url && (
                <div className="card" style={{ padding: '14px 18px' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                    {isVideo ? '🎬' : '🎙️'} {selected.title}
                    {selected.is_approved && <span style={{ marginInlineStart: 8, color: 'var(--primary)', fontSize: 12 }}>✔ مُراجَع ومعتمد</span>}
                  </div>
                  {isVideo ? (
                    <video ref={audioRef} src={selected.audio_url} controls playsInline preload="metadata"
                      onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
                      onLoadedMetadata={e => { setDuration(e.target.duration); e.target.playbackRate = playbackRate; }}
                      onEnded={() => setIsPlaying(false)}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onError={() => toast.error('تعذّر تحميل الملف')}
                      style={{ width: '100%', maxHeight: 360, background: '#000', borderRadius: 8 }}/>
                  ) : (
                    <audio ref={audioRef} src={selected.audio_url} controls preload="metadata"
                      onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
                      onLoadedMetadata={e => { setDuration(e.target.duration); e.target.playbackRate = playbackRate; }}
                      onEnded={() => setIsPlaying(false)}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onError={() => toast.error('تعذّر تحميل الملف')}
                      style={{ width: '100%' }}/>
                  )}
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>السرعة:</span>
                    {SPEEDS.map(s => (
                      <button key={s} onClick={() => setPlaybackRate(s)} className={`btn btn-sm ${playbackRate === s ? 'btn-primary' : 'btn-outline'}`} style={{ padding: '2px 8px', fontSize: 12 }}>{s}x</button>
                    ))}
                  </div>
                </div>
              )}

              <div className="card" style={{ padding: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <select className="form-select" value={selected.folder_id || ''} onChange={e => updateMeta({ folder_id: e.target.value || null })}>
                    <option value="">— بدون مجلّد —</option>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  <select className="form-select" value={selected.visibility || 'platform'} onChange={e => updateMeta({ visibility: e.target.value })}>
                    <option value="platform">يراها كل من له حساب</option>
                    <option value="private">خاصّة بي فقط</option>
                  </select>
                </div>
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {tags.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>لا وسوم — أنشئها من صفحة الوسوم</span>}
                  {tags.map(t => {
                    const active = (selected.tags || []).some(x => x.id === t.id);
                    return (
                      <button key={t.id} onClick={() => toggleTagOnSelected(t.id)} style={{
                        padding: '3px 10px', borderRadius: 12, border: '1px solid var(--border)',
                        background: active ? (t.color || 'var(--primary)') : 'var(--surface)',
                        color: active ? '#fff' : 'var(--text)', fontSize: 12, cursor: 'pointer',
                      }}>#{t.name}</button>
                    );
                  })}
                </div>
              </div>

              {selected.chunks_total > 0 && selected.chunks_done < selected.chunks_total && (
                <div className="card" style={{ padding: 14, background: 'var(--info-light)', borderColor: 'var(--info)' }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>🧩 ملف طويل — تفريغ على مراحل</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ flex: 1, height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${(selected.chunks_done / selected.chunks_total) * 100}%`, height: '100%', background: 'var(--primary)' }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.chunks_done}/{selected.chunks_total}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                    Groq يسمح بساعتين من الصوت/ساعة. كل جزء يُعالَج بفاصل ساعة.
                    {selected.next_chunk_at && (<> الجزء التالي: <strong>{new Date(selected.next_chunk_at).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}</strong></>)}
                  </div>
                  {isManager && (
                    <button className="btn btn-sm btn-primary" disabled={processing} onClick={async () => {
                      setProcessing(true);
                      try { await api(`/transcriptions/${selected.id}/process-next-chunk`, { method: 'POST' }); toast.success('بدأ تفريغ الجزء التالي'); selectTr({ id: selected.id }); }
                      catch (err) { toast.error(err.message); }
                      setProcessing(false);
                    }}>⚡ معالجة الجزء التالي الآن</button>
                  )}
                </div>
              )}

              {selected.summary && (
                <div className="card" style={{ borderInlineStart: '4px solid var(--accent)' }}>
                  <div className="card-title" style={{ marginBottom: 8 }}>📝 الملخّص</div>
                  <div style={{ lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{selected.summary}</div>
                  {keywords.length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {keywords.map((k, i) => (<span key={i} style={{ padding: '2px 10px', borderRadius: 12, background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 12 }}>#{k}</span>))}
                    </div>
                  )}
                </div>
              )}

              {chapters.length > 0 && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span>📑 فهرس يوتيوب — النقاط الأساسية مع توقيتها</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-primary" onClick={copyYoutubeChapters} title="انسخ الفهرس بصيغة يوتيوب لتلصقه في وصف الفيديو">📋 نسخ لليوتيوب</button>
                      <button className="btn btn-sm btn-outline" onClick={() => exportFile('chapters')}>تصدير</button>
                    </div>
                  </div>
                  <div>
                    {chapters.map((c, i) => (
                      <div key={i} onClick={() => seekToWord(c.start)} style={{ display: 'flex', gap: 12, padding: '8px 6px', borderBottom: i < chapters.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}>
                        <span style={{ fontFamily: 'monospace', color: 'var(--primary)', fontWeight: 600, minWidth: 60 }}>{formatTime(c.start)}</span>
                        <span>{c.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span className={`badge badge-${selected.status === 'completed' ? 'completed' : selected.status === 'processing' ? 'in_progress' : 'revision'}`}>
                      {selected.status === 'completed' ? (selected.is_approved ? '✔ مُعتمد' : 'مكتمل') : selected.status === 'processing' ? (selected.chunks_total > 0 ? `جزء ${selected.chunks_done}/${selected.chunks_total}` : 'جاري') : 'فشل'}
                    </span>
                    {selected.is_approved && selected.approver?.name && (<span style={{ fontSize: 11, color: 'var(--text-muted)' }}>اعتمده: {selected.approver.name}</span>)}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm btn-outline" onClick={() => exportFile('txt')}>📄 TXT</button>
                    <button className="btn btn-sm btn-outline" onClick={() => exportFile('docx')}>📝 Word</button>
                    <button className="btn btn-sm btn-outline" onClick={() => exportFile('srt')}>🎬 SRT</button>
                    <button className="btn btn-sm btn-outline" onClick={() => exportFile('pdf')}>🖨️ PDF</button>
                    <button className="btn btn-sm btn-outline" onClick={() => retranscribe(selected.id)} disabled={processing}>🔄 إعادة</button>
                    {selected.status === 'completed' && (<button className="btn btn-sm btn-outline" onClick={refreshAi} disabled={processing}>🤖 ملخّص جديد</button>)}
                    <button className="btn btn-sm btn-outline" onClick={openShares}>🔗 مشاركة</button>
                    <button className="btn btn-sm btn-outline" onClick={openRevisions}>🕓 المراجعات</button>
                    {selected.status === 'completed' && !selected.is_approved && (<button className="btn btn-sm" style={{ background: 'var(--primary)', color: '#fff' }} onClick={approveTr}>✔ اعتماد</button>)}
                    {selected.is_approved && isManager && (<button className="btn btn-sm btn-outline" onClick={unapproveTr}>إلغاء الاعتماد</button>)}
                    {editingText ? (
                      <button className="btn btn-sm btn-primary" onClick={async () => { await saveText(); setEditingText(false); }}>💾 حفظ</button>
                    ) : (
                      <button className="btn btn-sm btn-outline" onClick={() => setEditingText(true)} disabled={selected.status !== 'completed'}>✏️ تعديل</button>
                    )}
                    {isManager && (<button className="btn btn-sm btn-outline" style={{ color: 'var(--danger)', borderColor: 'var(--danger-light)' }} onClick={() => deleteTr(selected.id)}>🗑️</button>)}
                  </div>
                </div>

                {editingText ? (
                  <>
                    <textarea dir="rtl" className="form-textarea" value={editText} onChange={e => setEditText(e.target.value)} style={{ width: '100%', minHeight: 360, lineHeight: 2, fontSize: 15, fontFamily: 'inherit', resize: 'vertical' }}/>
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>سيُحفظ تاريخ التعديل في المراجعات</span>
                      <span>{editText.split(/\s+/).filter(Boolean).length} كلمة</span>
                    </div>
                  </>
                ) : (
                  <div dir="rtl" style={{ minHeight: 300, lineHeight: 2.4, fontSize: 15, overflowY: 'auto', maxHeight: 600, padding: '4px 6px', wordSpacing: 2, whiteSpace: 'pre-wrap' }}>
                    {alignedTokens.length === 0 ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>
                        {selected.status === 'completed' ? 'لا يوجد نص' : 'انتظر اكتمال التفريغ...'}
                      </div>
                    ) : alignedTokens.map((tok, i) => {
                      if (tok.isWhitespace) return tok.text;
                      const isActive = tok.start !== null && currentTime >= tok.start && currentTime < (tok.end ?? tok.start + 0.5);
                      return (
                        <span key={i} ref={isActive ? activeWordRef : null}
                          onClick={() => tok.start !== null && seekToWord(tok.start)}
                          title={tok.start !== null ? formatTime(tok.start) : ''}
                          style={{ cursor: tok.start !== null ? 'pointer' : 'default', background: isActive ? 'var(--accent-light)' : 'transparent', borderRadius: 3, padding: '1px 2px', fontWeight: isActive ? 600 : 400 }}>
                          {tok.text}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {refs.length > 0 && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 12 }}>📚 الآيات والأحاديث المُكتشَفة <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-faint)' }}>({refs.length})</span></div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {refs.map((r, i) => (
                      <div key={i} style={{ padding: '10px 12px', background: r.type === 'quran' ? 'var(--accent-light)' : 'var(--info-light)', borderInlineEnd: `4px solid ${r.type === 'quran' ? 'var(--accent)' : 'var(--info)'}`, borderRadius: 6, fontSize: 14, lineHeight: 1.8 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{r.type === 'quran' ? '﴾ آية قرآنية ﴿' : '« حديث نبوي »'}</div>
                        <div>{r.text}</div>
                        {(r.surah || r.ayah || r.source || r.narrator) && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            {[r.surah, r.ayah && `آية ${r.ayah}`, r.source, r.narrator].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showShareModal && selected && (<ShareModal shares={shares} onCreate={createShare} onDelete={deleteShare} onClose={() => setShowShareModal(false)} />)}
      {showRevisions && selected && (<RevisionsModal revisions={revisions} onRestore={restoreRevision} onClose={() => setShowRevisions(false)} />)}
    </div>
  );
}

function ShareModal({ shares, onCreate, onDelete, onClose }) {
  const [pwd, setPwd] = useState('');
  const [exp, setExp] = useState('');
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', padding: 24, borderRadius: 12, width: '100%', maxWidth: 500 }}>
        <h3 style={{ marginTop: 0 }}>🔗 روابط المشاركة العامّة</h3>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>شارك التفريغ مع من ليس لديه حساب — بكلمة سر اختيارية وانتهاء صلاحية اختياري.</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input className="form-input" placeholder="كلمة سر (اختياري)" value={pwd} onChange={e => setPwd(e.target.value)} style={{ flex: 1 }}/>
          <input className="form-input" placeholder="أيام" value={exp} onChange={e => setExp(e.target.value)} style={{ width: 100 }} type="number" min="1" max="365"/>
          <button className="btn btn-primary" onClick={() => { onCreate(pwd, exp); setPwd(''); setExp(''); }}>إنشاء</button>
        </div>
        <div>
          {shares.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>لا روابط مشاركة بعد</div>
          ) : shares.map(s => (
            <div key={s.id} style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 6, marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', direction: 'ltr', marginBottom: 4 }}>{window.location.origin}/share/{s.token}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                <span>{s.password_hash ? '🔒 محمي · ' : ''}مشاهدات: {s.view_count}{s.expires_at && <> · ينتهي: {new Date(s.expires_at).toLocaleDateString('ar-EG')}</>}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-sm btn-outline" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/share/${s.token}`)}>نسخ</button>
                  <button className="btn btn-sm btn-outline" style={{ color: 'var(--danger)' }} onClick={() => onDelete(s.id)}>🗑️</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, textAlign: 'left' }}><button className="btn btn-outline" onClick={onClose}>إغلاق</button></div>
      </div>
    </div>
  );
}

function RevisionsModal({ revisions, onRestore, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', padding: 24, borderRadius: 12, width: '100%', maxWidth: 700, maxHeight: '85vh', overflow: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>🕓 سجلّ المراجعات</h3>
        {revisions.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>لا مراجعات سابقة</div>
        ) : revisions.map(r => (
          <div key={r.id} style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 6, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(r.created_at).toLocaleString('ar-EG')} {r.user?.name && `· ${r.user.name}`}{r.note && <> · {r.note}</>}</span>
              <button className="btn btn-sm btn-outline" onClick={() => onRestore(r.id)}>استعادة</button>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.8, maxHeight: 200, overflow: 'auto', padding: 8, background: 'var(--gray-50)', borderRadius: 4, whiteSpace: 'pre-wrap' }}>{(r.formatted_text || '').slice(0, 600)}{(r.formatted_text || '').length > 600 ? '...' : ''}</div>
          </div>
        ))}
        <div style={{ textAlign: 'left', marginTop: 16 }}><button className="btn btn-outline" onClick={onClose}>إغلاق</button></div>
      </div>
    </div>
  );
}
